import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, MarkdownRenderer } from '../common';
import { useUpdateRoundSettings, useAdvanceRound } from '../../hooks/useRounds';
import { useUploadMedia } from '../../hooks/useSubmissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/x-m4a'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

export const SetupPhaseEditor = ({
  gameId,
  round,
  balance,
}) => {
  const fileInputRef = useRef(null);
  const updateSettings = useUpdateRoundSettings(gameId, round?.id);
  const advanceRound = useAdvanceRound(gameId, round?.id);
  const uploadMedia = useUploadMedia(gameId);

  const [passageLength, setPassageLength] = useState(round?.passageLength || 2);
  const [prompt, setPrompt] = useState({
    content: round?.prompt?.content || '',
    mediaUrl: round?.prompt?.mediaUrl || '',
    mediaType: round?.prompt?.mediaType || 'text',
  });
  const [passageTitle, setPassageTitle] = useState(round?.passageTitle || '');
  const [passageDescription, setPassageDescription] = useState(round?.passageDescription || '');
  const [uploadedFile, setUploadedFile] = useState(
    round?.prompt?.mediaUrl ? { name: 'Uploaded media', url: round.prompt.mediaUrl } : null
  );
  const [uploadError, setUploadError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Track if settings have changed from the saved round
  useEffect(() => {
    if (!round) return;
    const promptChanged =
      prompt.content !== (round.prompt?.content || '') ||
      prompt.mediaUrl !== (round.prompt?.mediaUrl || '') ||
      prompt.mediaType !== (round.prompt?.mediaType || 'text');
    const lengthChanged = passageLength !== round.passageLength;
    const passageMetaChanged =
      passageTitle !== (round.passageTitle || '') ||
      passageDescription !== (round.passageDescription || '');
    setHasUnsavedChanges(promptChanged || lengthChanged || passageMetaChanged);
  }, [round, prompt, passageLength, passageTitle, passageDescription]);

  const handleSaveSettings = async () => {
    setSaveError(null);
    try {
      await updateSettings.mutateAsync({
        passageLength,
        prompt: {
          content: prompt.content,
          mediaUrl: prompt.mediaUrl,
          mediaType: prompt.mediaType,
        },
        passageTitle: passageTitle.trim(),
        passageDescription: passageDescription.trim(),
      });
      setHasUnsavedChanges(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to save settings');
    }
  };

  const handleStartRound = async () => {
    setSaveError(null);
    try {
      // Save any pending changes first
      if (hasUnsavedChanges) {
        await updateSettings.mutateAsync({
          passageLength,
          prompt: {
            content: prompt.content,
            mediaUrl: prompt.mediaUrl,
            mediaType: prompt.mediaType,
          },
          passageTitle: passageTitle.trim(),
          passageDescription: passageDescription.trim(),
        });
      }
      // Advance to influence phase
      await advanceRound.mutateAsync();
      setShowConfirmModal(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to start round');
      setShowConfirmModal(false);
    }
  };

  const handleMediaTypeSelect = (type) => {
    setPrompt({ ...prompt, mediaType: type, mediaUrl: '' });
    setUploadedFile(null);
    setUploadError(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadError(null);

    const allowedTypes = prompt.mediaType === 'image' ? ALLOWED_IMAGE_TYPES : ALLOWED_AUDIO_TYPES;
    const maxSize = prompt.mediaType === 'image' ? MAX_IMAGE_SIZE : MAX_AUDIO_SIZE;

    if (!allowedTypes.includes(file.type)) {
      setUploadError(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }

    if (file.size > maxSize) {
      setUploadError(`File too large. Maximum: ${maxSize / 1024 / 1024}MB`);
      return;
    }

    try {
      const result = await uploadMedia.mutateAsync({ file, type: prompt.mediaType });
      setUploadedFile({
        name: file.name,
        url: result.media.url,
      });
      setPrompt({ ...prompt, mediaUrl: result.media.url });
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      console.error('Upload failed:', err);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveMedia = () => {
    setUploadedFile(null);
    setPrompt({ ...prompt, mediaUrl: '' });
  };

  const hasEnoughInfluence = balance >= passageLength;
  const canStartRound = prompt.content.trim().length > 0 && hasEnoughInfluence;

  // Format timestamp for display
  const formatTimestamp = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const promptTimestamp = round?.prompt?.createdAt;

  return (
    <div className="space-y-4">
      {/* Editable indicator */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Round Setup</h2>
        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded">
          Editable
        </span>
      </div>

      {saveError && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg">
          {saveError}
        </div>
      )}

      {/* Passage Title & Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passage Title <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={passageTitle}
            onChange={(e) => setPassageTitle(e.target.value)}
            placeholder="An (optional) title for this passage"
            maxLength={200}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Passage Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={passageDescription}
            onChange={(e) => setPassageDescription(e.target.value)}
            placeholder="An (optional) description for this passage"
            maxLength={500}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Passage Length */}
      <div className="bg-gray-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Passage Length (L)
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="1"
            max="5"
            value={passageLength}
            onChange={(e) => setPassageLength(parseInt(e.target.value))}
            className="flex-1"
          />
          <span className="text-2xl font-bold text-primary-600 w-8 text-center">
            {passageLength}
          </span>
        </div>
        <p className={`text-sm mt-2 ${hasEnoughInfluence ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
          {hasEnoughInfluence
            ? `Cost: ${passageLength} influence will be added to the pot when you start the round`
            : `Insufficient influence! You need ${passageLength} but only have ${balance}. Lower L or wait for more influence.`
          }
        </p>
      </div>

      {/* Prompt */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Your Prompt
          </label>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
            <button
              type="button"
              className={`px-3 py-1 transition-colors ${
                !previewMode
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setPreviewMode(false)}
            >
              Write
            </button>
            <button
              type="button"
              className={`px-3 py-1 transition-colors ${
                previewMode
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setPreviewMode(true)}
            >
              Preview
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          Supports <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Markdown</a>
          {promptTimestamp && <span>. Last changed at {formatTimestamp(promptTimestamp)}</span>}
        </p>
        {previewMode ? (
          <div className="w-full min-h-[200px] px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 overflow-auto">
            {prompt.content ? (
              <MarkdownRenderer content={prompt.content} />
            ) : (
              <p className="text-gray-400 italic">Nothing to preview yet...</p>
            )}
          </div>
        ) : (
          <textarea
            value={prompt.content}
            onChange={(e) => setPrompt({ ...prompt, content: e.target.value })}
            placeholder="Set the stage for this round. Describe the situation, assert details (up to L), and prompt action from your players..."
            rows={8}
            maxLength={5000}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
          />
        )}
        <div className="flex justify-between text-sm text-gray-500 mt-1">
          <span>Set the scene and prompt your players</span>
          <span>{prompt.content.length}/5000</span>
        </div>
      </div>

      {/* Media attachment */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Attach Media (optional)</h4>
        <div className="flex gap-4">
          <button
            type="button"
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              prompt.mediaType === 'image'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMediaTypeSelect('image')}
          >
            <span className="block text-2xl mb-1">🖼️</span>
            <span className="text-sm">Image</span>
          </button>
          <button
            type="button"
            className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
              prompt.mediaType === 'audio'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleMediaTypeSelect('audio')}
          >
            <span className="block text-2xl mb-1">🎵</span>
            <span className="text-sm">Audio</span>
          </button>
        </div>

        {(prompt.mediaType === 'image' || prompt.mediaType === 'audio') && (
          <div className="mt-3 space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={prompt.mediaType === 'image'
                ? ALLOWED_IMAGE_TYPES.join(',')
                : ALLOWED_AUDIO_TYPES.join(',')}
              onChange={handleFileSelect}
              disabled={uploadMedia.isPending}
              className="hidden"
            />

            {uploadedFile ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {prompt.mediaType === 'image' ? (
                  <img
                    src={getMediaUrl(uploadedFile.url)}
                    alt={uploadedFile.name}
                    className="w-16 h-16 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                  <p className="text-xs text-green-600">Uploaded successfully</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveMedia}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMedia.isPending}
                loading={uploadMedia.isPending}
              >
                {uploadMedia.isPending ? 'Uploading...' : `Upload ${prompt.mediaType === 'image' ? 'Image' : 'Audio'}`}
              </Button>
            )}

            {uploadError && (
              <p className="text-sm text-red-600">{uploadError}</p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t">
        {hasUnsavedChanges && (
          <Button
            variant="secondary"
            onClick={handleSaveSettings}
            loading={updateSettings.isPending}
          >
            Save
          </Button>
        )}
        {!hasUnsavedChanges && <div />}

        <Button
          onClick={() => setShowConfirmModal(true)}
          disabled={!canStartRound}
        >
          Start Round
        </Button>
      </div>

      {!canStartRound && (
        <p className="text-sm text-amber-600 text-center">
          {!prompt.content.trim()
            ? 'Enter a prompt to start the round'
            : `You need ${passageLength} influence to start (you have ${balance})`
          }
        </p>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Start Round?"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Starting the round will spend <strong>{passageLength} influence</strong> from your balance
            (currently {balance}) and lock these settings.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-gray-500">Passage Length:</span>
              <span className="font-medium">{passageLength}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Your Balance After:</span>
              <span className="font-medium">{balance - passageLength}</span>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStartRound}
              loading={advanceRound.isPending || updateSettings.isPending}
            >
              Start Round
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
